// Engagement closure (store tracker 6.55) — the inline Review & Submit card.
//
// Renders once, under the turn whose response carried the completion signal
// (platform: "Completion closes the conversational loop"). Reuses the start
// card's visual language (icon tile, title/sub, one action) rather than
// inventing a second card shape for what is functionally the same "here's an
// affordance, tap it" pattern.
//
// This card is a one-time nudge; the pinned card's own primary affordance
// (`PinnedFormCard`, driven by `formsSelectors.isFormComplete`) is what keeps
// the exit reachable after this card scrolls out of view — that state is
// derived live from stored values, not from this message having been seen.
//
// The verbal switch (Bot.API: an explicit chat request for a queued form
// activates it on the visitor's own turn, and the same response can ALSO
// carry `form_complete` for the form that switch just paused) means `formId`
// here is not guaranteed to name the form currently pinned — this card can
// legitimately name a form sitting in the queue. Review must always target
// the form THIS card names, never whatever happens to be active: when
// `formId` IS the active form, Review opens the overlay directly (unchanged
// behavior); otherwise Review re-activates `formId` through the ordinary
// Switch path (`switchToForm`) instead of opening the (wrong) active form's
// view — the server answers an already-complete form's activation with the
// completion signal again (existing contract), which repins `formId` and
// lets the normal turn loop produce a fresh, correctly-bound card. No form
// active at all (state-hydration edge, before `activeFormId` settles) is
// treated the same as "some other form is active": the card still renders,
// and Review's switch establishes activation from scratch — hiding a card
// for a form that is otherwise perfectly reviewable would be the wrong
// failure mode.

import styles from './ConversationalForms.module.css';
import { useConversationalForms } from './FormsContext';
import { CheckIcon } from './icons';

const ReviewSubmitCard = ({ formId }: { formId: string }) => {
    const { registry, state, switchToForm, openFormView } = useConversationalForms();

    const form = registry?.byId.get(formId);
    if (!form) return null;
    // A late-reconciled or replayed message must never resurrect this card
    // for a form the student has already submitted.
    if (state.submittedFormIds.includes(formId)) return null;
    // Nor for one discarded since this message landed — the values are gone
    // and a Review tap that survives past a Discard must not resurrect it
    // via `switchToForm` (which now reaches a server-named id, same
    // suppression `activate` already applies).
    if (state.discardedFormIds.includes(formId)) return null;

    const isActiveForm = state.activeFormId === formId;
    const onReview = isActiveForm ? openFormView : () => switchToForm(formId);

    return (
        <div className={styles.startCard} data-testid="cf-review-card">
            <span className={styles.startIcon}>
                <CheckIcon size={17} />
            </span>
            <span className={styles.startText}>
                <span className={styles.startTitle}>{form.short} is ready</span>
                <span className={styles.startSub}>Review your answers and submit.</span>
            </span>
            <button type="button" className={styles.startButton} onClick={onReview}>
                Review
            </button>
        </div>
    );
};

export default ReviewSubmitCard;
